import { useQuery } from "@tanstack/react-query"
import { useMemo } from "react"
import { Except } from "type-fest"
import {
  digitalInspectionReportDetails,
  digitalInspectionReportDetailsMulti
} from "src/graphql/custom/diReport"
import { API } from "src/shared/lib/amplify"
import { isEmpty } from "ramda"
export interface DigitalInspectionReportPayload {
  isApiEnabled: boolean
  buildingId: string
  equipment: string
  systemOrEquipmentType: string
  barScale: number
  startDate: string
  endDate: string
  checkedAts: string[]
  offeringCodes: string[]
  reportType?: string // Add reportType to determine DI_MULTI vs regular reports
}

function payloadMapper(
  payload: Except<DigitalInspectionReportPayload, "isApiEnabled">
) {
  return {
    bId: payload.buildingId ?? "",
    eId: payload.equipment ?? "",
    eType: payload.systemOrEquipmentType ?? "",
    bScale: payload.barScale ?? "",
    startDate: payload.startDate ?? "",
    endDate: payload.endDate ?? "",
    chkAts: payload.checkedAts ?? [],
    offeringCodes: payload.offeringCodes ?? []
  }
}

function queryFn(
  payload: Except<DigitalInspectionReportPayload, "isApiEnabled">
) {
  return async function () {
    // Use different mutation based on report type
    const isMultiDIReport = payload?.reportType === "DI_MULTI"
    const query = isMultiDIReport
      ? digitalInspectionReportDetailsMulti
      : digitalInspectionReportDetails

    const response = await API.graphql<any>({
      query: query,
      variables: { body: JSON.stringify(payloadMapper(payload ?? ({} as any))) }
    })

    return response

    // // Mock to check whether the data atom works fine
    // const mData = payload.equipmentId === "9928ad3c-6896-4118-ae8a-7b0f7d066e2e" ? mockApiData_1 : mockApiData_2

    // const response = await Promise.resolve({ ...mData })
    // return response
  }
}

/**
 * useApiDigitalInspectionReport
 *
 * @param payload
 * @returns
 */
export default function useApiDigitalInspectionReport(payload) {
  const {
    buildingId,
    equipment,
    systemOrEquipmentType,
    startDate,
    endDate,
    barScale,
    checkedAts,
    enabled,
    offeringCodes,
    reportType
  } = payload ?? {}

  const checkedAtsKey = (isEmpty(checkedAts) ? ["All"] : checkedAts).join("-")

  const hasRequiredParams = !!(
    buildingId &&
    equipment &&
    systemOrEquipmentType &&
    startDate &&
    endDate &&
    barScale &&
    enabled
  )

  const queryKey = [
    "Digital inspection Report",
    systemOrEquipmentType,
    buildingId,
    equipment,
    startDate,
    endDate,
    barScale,
    checkedAtsKey,
    offeringCodes
  ]

  const result = useQuery({
    enabled: hasRequiredParams,
    queryKey,
    queryFn: queryFn({
      buildingId,
      equipment,
      systemOrEquipmentType,
      startDate,
      endDate,
      barScale,
      checkedAts,
      offeringCodes,
      reportType
    }),
    staleTime: 1000 * 60 * 60 * 4, // 4 hrs,
    select: (response: any) => {
      try {
        // For multi DI reports, use generateDISumary response, otherwise use generateDIReport
        const isMultiDIReport = reportType === "DI_MULTI"
        const data = isMultiDIReport
          ? response?.data?.generateDISumary ?? null
          : response?.data?.generateDIReport ?? null

        const res = JSON.parse(data)

        return res?.body?.data ?? {}

        // return JSON.parse(response?.userData)
      } catch (error) {
        console.log(
          "Unable to parse the digital inspection report api response."
        )
        return null
      }
    }
  })

  const { isPending, isFetching } = result

  const isApiLoading = isPending && isFetching

  return useMemo(
    () => Object.assign(result, { isApiLoading }),
    [result.data, isApiLoading]
  )
}

